import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Link } from 'react-router-dom';
import { Tag } from '@verclam/ui-kit';

const rowStyle = (hasMeta: boolean, isDimmed: boolean) => css`
	display: grid;
	grid-template-columns: ${
		hasMeta ? 'minmax(0, 1.2fr) minmax(0, 1.5fr) auto auto' : 'minmax(0, 1.2fr) minmax(0, 1.5fr) auto'
	};
	gap: 1.125rem;
	align-items: center;
	padding: 1.125rem 1.25rem;
	border-radius: ${theme.radii.large};
	border: 1.5px solid ${theme.colors.border};
	background-color: ${isDimmed ? theme.colors.surfaceInset : theme.colors.surface};
	color: ${theme.colors.ink};
	text-decoration: none;

	&:hover {
		border-color: ${theme.colors.accent};
	}

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: minmax(0, 1fr);
		gap: 0.5rem;
		justify-items: start;
	}
`;

const identityStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	min-width: 0;
`;

const nameStyle = (isDimmed: boolean) => css`
	${theme.typography.cardTitle}
	color: ${isDimmed ? theme.colors.inkMuted : theme.colors.ink};
`;

const descriptionStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const summaryStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.5;
	color: ${theme.colors.inkSecondary};
`;

const metaStyle = css`
	font-family: ${theme.fonts.mono};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.4;
	color: ${theme.colors.inkMuted};
	white-space: nowrap;
`;

type Props = {
	readonly to: string;
	readonly ariaLabel: string;
	readonly name: string;
	readonly description: string | null;
	/** What the workflow does, in one line. */
	readonly summary: string;
	/** Version line; absent for a workflow that has none to show. */
	readonly meta?: string;
	readonly isActive: boolean;
	readonly inactiveLabel: string;
};

/** One workflow of the list: a whole-row link to its screen with its name, summary, version and state. */
const WorkflowRow: React.FC<Props> = ({
	to,
	ariaLabel,
	name,
	description,
	summary,
	meta,
	isActive,
	inactiveLabel,
}) => {
	const isDimmed = !isActive;

	return (
		<Link to={to} aria-label={ariaLabel} css={rowStyle(meta !== undefined, isDimmed)}>
			<span css={identityStyle}>
				<span css={nameStyle(isDimmed)}>{name}</span>
				<ConditionalLoader data={description !== null && description !== ''}>
					<span css={descriptionStyle}>{description}</span>
				</ConditionalLoader>
			</span>
			<span css={summaryStyle}>{summary}</span>
			<ConditionalLoader data={meta}>
				{(metaText) => <span css={metaStyle}>{metaText}</span>}
			</ConditionalLoader>
			<Tag
				value={isActive ? 'Actif' : inactiveLabel}
				severity={isActive ? 'success' : 'secondary'}
				rounded
			/>
		</Link>
	);
};

export default WorkflowRow;
